import React, { useState } from 'react';
import { doc, updateDoc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getInverseKey } from '../../utils/referenceInverse';
import { getKeyNote } from '../../utils/keyRegistry';
import Tooltip from '../Tooltip';
import './DisplayItemsList.css';

/**
 * DisplayItemsList — renders existing displayItems as arrow-bordered cards
 * with ↑/↓ reorder and delete (with confirmation).
 *
 * Arrow card pattern: | key >> value | [×]
 * - Left card (key): flat left border, right-pointing chevron on right edge
 * - Right card (value): receives the chevron (flush), flat right border
 *
 * Props:
 *   entryId — Firestore doc ID
 *   items — array of displayItem objects
 *   onUpdated(newItems) — called after reorder or delete with the updated array
 *   readOnly — when true, hides reorder/delete controls (learner view)
 *   showKeyNotes — when true (learner view), renders a ⓘ affordance next to
 *     keys that have a registry keyNote, revealing the note inline on tap/click
 *   language — the entry's language, scoping the keyNote lookup (default 'fr')
 */
function DisplayItemsList({ entryId, items, onUpdated, readOnly, onLinkedEntryClick, showKeyNotes = false, language }) {
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [noteOpenIndex, setNoteOpenIndex] = useState(null);

  if (!items || items.length === 0) return null;

  const saveItems = async (newItems) => {
    try {
      const entryRef = doc(db, 'dictionary', entryId);
      await updateDoc(entryRef, {
        displayItems: newItems,
        lastUpdated: serverTimestamp(),
      });
      onUpdated && onUpdated(newItems);
    } catch (e) {
      console.error('Error updating displayItems:', e);
    }
  };

  const moveUp = (index) => {
    if (index <= 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    saveItems(newItems);
  };

  const moveDown = (index) => {
    if (index >= items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    saveItems(newItems);
  };

  const removeItem = async (index) => {
    const item = items[index];
    const newItems = items.filter((_, i) => i !== index);
    const inverseKey = item && item.type === 'text' ? getInverseKey(item.key) : null;

    // Bidirectional delete: if the removed item has an inverse-mapped key and an
    // entryId, also remove the matching inverse from the referenced entry.
    if (inverseKey && item.entryId) {
      try {
        const refRef = doc(db, 'dictionary', item.entryId);
        const refSnap = await getDoc(refRef);
        const batch = writeBatch(db);
        batch.update(doc(db, 'dictionary', entryId), { displayItems: newItems, lastUpdated: serverTimestamp() });
        if (refSnap.exists()) {
          const refItems = (refSnap.data().displayItems || []).filter(
            (di) => !(di.type === 'text' && di.key === inverseKey && di.entryId === entryId)
          );
          batch.update(refRef, { displayItems: refItems, lastUpdated: serverTimestamp() });
        }
        await batch.commit();
        onUpdated && onUpdated(newItems);
        setConfirmIndex(null);
        return;
      } catch (e) {
        console.error('Error removing bidirectional item:', e);
      }
    }

    // Fall through: normal single-doc delete
    saveItems(newItems);
    setConfirmIndex(null);
  };

  const getItemDisplay = (item) => {
    if (item.type === 'label') {
      return { key: item.text, value: null };
    }
    if (item.type === 'text') {
      return { key: item.key, value: item.value, entryId: item.entryId || null };
    }
    if (item.type === 'reference') {
      return { key: item.relationship, value: item.label, entryId: item.entryId, note: item.note };
    }
    if (item.type === 'composition') {
      const labels = (item.components || []).map((c) => c.label).join(' + ');
      return { key: item.key, value: labels };
    }
    return { key: '?', value: JSON.stringify(item) };
  };

  return (
    <div className="display-items-list">
      <p className="display-items-list-label">Annotations:</p>
      {items.map((item, index) => {
        const display = getItemDisplay(item);
        const isConfirming = confirmIndex === index;
        const keyNote = showKeyNotes ? getKeyNote(display.key, language || 'fr') : null;
        const isNoteOpen = noteOpenIndex === index;

        return (
          <div key={index} className={`display-items-list-row ${isConfirming ? 'display-items-list-row--confirming' : ''}`}>
            {!isConfirming ? (
              <>
                <div className="arrow-card-pair">
                  <div className="arrow-card arrow-card--key">
                    <span className="arrow-card-text">{display.key}</span>
                  </div>
                  {display.value && (
                    <div
                      className={`arrow-card arrow-card--value ${display.entryId ? 'arrow-card--linked' : ''}`}
                      onClick={display.entryId && onLinkedEntryClick ? () => onLinkedEntryClick(display.entryId) : undefined}
                      role={display.entryId && onLinkedEntryClick ? 'button' : undefined}
                      tabIndex={display.entryId && onLinkedEntryClick ? 0 : undefined}
                      style={display.entryId && onLinkedEntryClick ? { cursor: 'pointer' } : undefined}
                    >
                      <span className="arrow-card-text">{display.value}</span>
                    </div>
                  )}
                  {keyNote && (
                    <button
                      type="button"
                      className="display-items-list-keynote-btn"
                      aria-label={`What does "${display.key}" mean?`}
                      aria-expanded={isNoteOpen}
                      onClick={() => setNoteOpenIndex(isNoteOpen ? null : index)}
                    >ⓘ</button>
                  )}
                </div>
                {keyNote && isNoteOpen && (
                  <div className="display-items-list-keynote">{keyNote}</div>
                )}
                {item.note && (
                  <div className="arrow-card-note">↳ {item.note}</div>
                )}
                {!readOnly && (
                <div className="display-items-list-controls">
                  <Tooltip label="Move up">
                    <button
                      className="display-items-list-btn"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      aria-label="Move up"
                    >↑</button>
                  </Tooltip>
                  <Tooltip label="Move down">
                    <button
                      className="display-items-list-btn"
                      onClick={() => moveDown(index)}
                      disabled={index === items.length - 1}
                      aria-label="Move down"
                    >↓</button>
                  </Tooltip>
                  <Tooltip label="Remove annotation">
                    <button
                      className="display-items-list-btn display-items-list-btn--delete"
                      onClick={() => setConfirmIndex(index)}
                      aria-label="Remove annotation"
                    >×</button>
                  </Tooltip>
                </div>
                )}
              </>
            ) : (
              <div className="display-items-list-confirm">
                <span className="display-items-list-confirm-text">Remove this annotation?</span>
                <div className="display-items-list-confirm-actions">
                  <button
                    className="display-items-list-confirm-yes"
                    onClick={() => removeItem(index)}
                  >
                    Remove
                  </button>
                  <button
                    className="display-items-list-confirm-no"
                    onClick={() => setConfirmIndex(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default DisplayItemsList;
